import * as React from 'react';
import * as _ from 'underscore';

import { LeftNavLinkNavigationEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ILogo, LogoService } from '../../ts/services/LogoService';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { ServerUrls } from '../ServerUrls';
import { ServiceLocator } from '../ServiceLocator';
import { SpacesSiteUrls } from '../spaces/SpacesSiteUrls';
import { ConnectedSitePicker } from './ConnectedSitePicker';
import { ContentLinks, ServerLinks, SiteLinks } from './NavLinkDefinitions';
import { NavList, NavListItem, NavListItemRenderProp, NavListItemState, NavListProps } from './NavList';
import { NavLocation } from './NavLocation';
import { NavPanelLogo } from './NavPanelLogo';
import { NavSectionSeparator } from './NavSectionSeparator';
import { SpaceType } from '../state/SpaceInfo';

export type NavPanelDisplayMode = 'icon' | 'iconAndText';

export interface NavPanelOwnProps {
  readonly navPanelDisplayMode: NavPanelDisplayMode;
  readonly serverUrls: ServerUrls;
  readonly onSitePickerClick?: () => void;
  readonly onSelectionMade?: () => void;
}

export interface NavPanelStateProps {
  readonly siteUrls: SpacesSiteUrls;
  readonly spaceType: SpaceType;
  readonly currentNavLocation: NavLocation;
  readonly inServerArea: boolean;

  readonly isSessionSiteAdmin: boolean;
  readonly isSessionServerAdmin: boolean;

  readonly isSessionMultiSite: boolean;
  readonly isSiteAdminSettingsEnabled: boolean;
  readonly isAdminViewsEnabled: boolean;

  readonly isCatalogingOnSiteEnabled: boolean;
  readonly isNamedSharingEnabled: boolean;
  readonly isVizRecommendationsEnabled: boolean;
  readonly shouldShowPersonalSpace: boolean;
}

export type NavPanelProps = NavPanelOwnProps & NavPanelStateProps;

export const LogoWrapperDivTestId = 'logo-div-wrapper';
export class NavPanel extends React.PureComponent<NavPanelProps> {

  public render(): JSX.Element {
    return (
      <nav role="navigation" data-tb-test-id="nav-panel">
        {this.renderLogo()}
        {this.shouldShowSitePicker() && this.renderSitePicker()}
        {this.navList()}
      </nav>
    );
  }

  private navList(): JSX.Element {
    const items: NavListItem[] = this.shouldShowServerLinks() ? this.serverAdminLinks() : this.siteLinks();
    const props: NavListProps = {
      items: items,
      selectedValue: this.props.currentNavLocation,
      onSelect: this.onNavListSelect
    };

    return <NavList  {...props}/>;
  }

  private onNavListSelect = (value: string) => {
    Telemetry.sendEvent<LeftNavLinkNavigationEvent>({
      type: TelemetryEvents.LeftNavLinkNavigation,
      navLocation: value,
      previousSpace: this.props.spaceType,
    });
    this.onSelection();
  }

  private siteLinks(): NavListItem[] {
      const items = this.getSiteContentLinks();
      if (this.isSessionSiteOrServerAdmin()) {
        return items.concat(this.siteAdminLinks());
      }
      return items;
  }

  private getSiteContentLinks(): NavListItem[] {
    if (FeatureFlags.collectionsEnabled) {
      return this.newSiteContentLinksWithCollections();
    } else {
      return this.siteContentLinks();
    }
  }

  private siteContentLinks(): NavListItem[] {
    return _.compact([
      FeatureFlags.homePage && {
        content: ContentLinks.HomeNavListLink(this.props),
        value: 'home'
      },
      this.props.shouldShowPersonalSpace && {
        content: ContentLinks.PersonalSpaceNavListLink(this.props),
        value: 'personalSpace'
      },
      {
        content: ContentLinks.ExploreNavListLink(this.props),
        value: 'explore'
      },
      {
        content: ContentLinks.FavoritesNavListLink(this.props),
        value: 'favorites'
      },
      {
        content: ContentLinks.RecentsNavListLink(this.props),
        value: 'recents'
      },
      FeatureFlags.sharedWithMeBasicEnabled && this.props.isNamedSharingEnabled && {
        content: ContentLinks.SharedWithMeNavListLink(this.props),
        value: 'sharedWithMe'
      },
      this.props.isVizRecommendationsEnabled && {
        content: ContentLinks.RecommendationsNavListLink(this.props),
        value: 'recommendations'
      },
      this.props.isCatalogingOnSiteEnabled && {
        content: ContentLinks.CatalogNavListLink(this.props),
        value: 'catalog'
      },
    ]);
  }

  private newSiteContentLinksWithCollections(): NavListItem[] {
    return _.compact([
      FeatureFlags.homePage && {
        content: ContentLinks.HomeNavListLink(this.props),
        value: 'home'
      },
      this.props.shouldShowPersonalSpace && {
        content: ContentLinks.PersonalSpaceNavListLink(this.props),
        value: 'personalSpace'
      },
      {
        content: ContentLinks.FavoritesNavListLink(this.props),
        value: 'favorites'
      },
      {
        content: ContentLinks.RecentsNavListLink(this.props),
        value: 'recents'
      },
      FeatureFlags.sharedWithMeBasicEnabled && this.props.isNamedSharingEnabled && {
        content: ContentLinks.SharedWithMeNavListLink(this.props),
        value: 'sharedWithMe'
      },
      {
        content: this.separatorNavListLink(this.props.navPanelDisplayMode),
        value: '',
        disabled: true,
      },
      {
        content: ContentLinks.ExploreNavListLink(this.props),
        value: 'explore'
      },
      FeatureFlags.collectionsEnabled && {
        content: ContentLinks.CollectionsNavListLink(this.props),
        value: 'collections'
      },
      this.props.isVizRecommendationsEnabled && {
        content: ContentLinks.RecommendationsNavListLink(this.props),
        value: 'recommendations'
      },
      this.props.isCatalogingOnSiteEnabled && {
        content: this.separatorNavListLink(this.props.navPanelDisplayMode),
        value: '',
        disabled: true,
      },
      this.props.isCatalogingOnSiteEnabled && {
        content: ContentLinks.CatalogNavListLink(this.props),
        value: 'catalog'
      },
    ]);
  }

  private separatorNavListLink = (navPanelDisplayMode: NavPanelDisplayMode): NavListItemRenderProp => {
    return function(navListItemState: NavListItemState): JSX.Element {
      return <NavSectionSeparator navPanelDisplayMode={navPanelDisplayMode} />;
    };
  }

  private siteAdminLinks(): NavListItem[] {
    return _.compact([
      {
        content: this.separatorNavListLink(this.props.navPanelDisplayMode),
        value: '',
        disabled: true,
      },
      {
        content: SiteLinks.UsersNavListLink(this.props),
        value: 'site_users'
      },
      {
        content: SiteLinks.GroupsNavListLink(this.props),
        value: 'site_groups'
      },
      {
        content: SiteLinks.SchedulesNavListLink(this.props),
        value: 'site_schedules'
      },
      FeatureFlags.jobManagementEnabled && {
        content: SiteLinks.JobsNavListLink(this.props),
        value: 'site_jobs'
      },
      {
        content: SiteLinks.TasksNavListLink(this.props),
        value: 'site_tasks'
      },
      this.statusLinkForSitePages(),
      this.settingLinkForSitePages()
    ]);
  }

  private serverAdminLinks(): NavListItem[] {
    return _.compact([
      {
        content: ServerLinks.SitesNavListLink(this.props),
        value: 'server_sites'
      },
      {
        content: ServerLinks.UsersNavListLink(this.props),
        value: 'server_users'
      },
      {
        content: ServerLinks.SchedulesNavListLink(this.props),
        value: 'server_schedules'
      },
      FeatureFlags.jobManagementEnabled && {
        content: ServerLinks.JobsNavListLink(this.props),
        value: 'server_jobs'
      },
      {
        content: ServerLinks.TasksNavListLink(this.props),
        value: 'server_tasks'
      },
      {
        content: ServerLinks.StatusNavListLink(this.props),
        value: 'server_status'
      },
      {
        content: ServerLinks.SettingsNavListLink(this.props),
        value: 'server_settings'
      }
    ]);
  }

  private renderSitePicker() {
    return (
      <ConnectedSitePicker
        navPanelDisplayMode={this.props.navPanelDisplayMode}
        serverUrls={this.props.serverUrls}
        onSitePickerClick={this.props.onSitePickerClick}
        onSelection={this.onSelection}
      />
    );
  }

  private shouldShowServerLinks() {
    return this.props.inServerArea && this.props.isSessionMultiSite;
  }

  private isSessionSiteOrServerAdmin(): boolean {
    return this.props.isSessionSiteAdmin || this.props.isSessionServerAdmin;
  }

  private getCorrectSettingsLinkToShowOnSiteForServerAdmin(): NavListItem {
    if (this.props.isSessionMultiSite) {
      return {
        content: SiteLinks.SettingsNavListLink(this.props),
        value: 'site_settings'
      };
    } else {
      // server admins on a server with only one site, get the server settings page
      return {
        content: ServerLinks.SettingsNavListLink(this.props),
        value: 'server_settings'
      };
    }
  }

  private settingLinkForSitePages(): NavListItem {
    if (this.props.isSessionServerAdmin) {
      return this.getCorrectSettingsLinkToShowOnSiteForServerAdmin();
    } else if (this.props.isSessionSiteAdmin && this.props.isSiteAdminSettingsEnabled) {
      return {
        content: SiteLinks.SettingsNavListLink(this.props),
        value: 'site_settings'
      };
    }

    return undefined;
  }

  private statusLinkForSitePages(): NavListItem {
    if (this.props.isSessionMultiSite || this.props.isSessionSiteAdmin) {
      if (this.props.isAdminViewsEnabled) {
        return {
          content: SiteLinks.StatusNavListLink(this.props),
          value: 'site_status'
        };
      } else {
        return undefined;
      }
    } else { // server admin on server with one site
      return {
        content: ServerLinks.StatusNavListLink(this.props),
        value: 'server_status'
      };
    }
  }

  private shouldShowSitePicker(): boolean {
    return this.props.isSessionMultiSite;
  }

  private renderLogo(): JSX.Element {
    const logo: ILogo = ServiceLocator.get<LogoService>('LogoService').getNavPanelLogo();
    const compactLogo: ILogo = ServiceLocator.get<LogoService>('LogoService').getCompactLogo();
    const collapsed = this.props.navPanelDisplayMode === 'icon';

    return <NavPanelLogo logo={logo} compactLogo={compactLogo} siteUrls={this.props.siteUrls} collapsed={collapsed} />;
  }

  private onSelection = () => {
    if (this.props.onSelectionMade) {
      this.props.onSelectionMade();
    }
  }
}
